var angular = require('angular');
var moment = require('js/lib/moment');

var t = require('js/lib/translate').t;
var Customer = require('./customer');
var Route = require('./route');
var Count = require('./count');
var SuggestedRoute = require('./suggestedRoute');
var File = require('./file');
var util = require('./util');

function RoutePlan(routePlan, light) {
  angular.extend(this, routePlan);
  if(this.date) {
    this.date = this.date.slice(0, 10); // YYYY-MM-DD
  }
  if (! light) {
    this.customers = (this.customers || []).map(function(customer) {
      return new Customer(customer);
    });

    this.routes = (this.routes || []).map(function(route) {
      return new Route(route, this.customers.filter(function(customer) {
        return customer.routeId === route.id;
      }));
    }, this);

    this.suggestedRoutes = (this.suggestedRoutes || []).sort(function(r1, r2) {
      return r1.name.localeCompare(r2.name);
    }).map(function(suggestedRoute) {
      return new SuggestedRoute(suggestedRoute);
    });

    this.files = (this.files || []).map(function(file) {
      return new File(file);
    }).sort(function(a, b) {
      return a.id - b.id;
    });
  }
}

RoutePlan.prototype.hasAccessToAllCustomerSystems = function(customerSystemIds) {
  return this.customerSystems.every(function(customerSystemId) {
    // Check if every CS for the plan is available in the given list
    return customerSystemIds.some(function(csId) {
      return csId === customerSystemId;
    });
  });
};

RoutePlan.prototype.hasRoutes = function() {
  return this.routes && this.routes.length !== 0;
};

RoutePlan.prototype.suggestedRoutesCount = function() {
  return this.suggestedRoutes.length;
};

RoutePlan.prototype.suggestedRoutesCoverage = function() {
  var totalCount = this.suggestedRoutesTotalCount();
  return 100 * (totalCount - this.suggestedRoutesUnplaced()) / totalCount;
};

RoutePlan.prototype.suggestedRoutesUnplaced = function() {
  return this.suggestedRoutes.reduce(function(count, sugRoute) {
    return count + (sugRoute.name.trim() === '' ? sugRoute.customerCount : 0);
  }, 0);
};

RoutePlan.prototype.suggestedRoutesTotalCount = function() {
  return this.suggestedRoutes.reduce(function(count, sugRoute) {
    return count + sugRoute.customerCount;
  }, 0);
};

RoutePlan.prototype.unlinkedCustomers = function() {
  return this.customers.filter(function(customer) {
    return ! customer.routeId;
  });
};

RoutePlan.prototype.validStats = function() {
  return this.routes.every(function(route) {
    return route.validStats();
  });
};

RoutePlan.prototype.noRoutesAreOptimizing = function() {
  return this.routes.every(function(route) {
    return (route.optimizationStatus === -1 || route.optimizationStatus === 0); // Dirty or OK
  });
};

RoutePlan.prototype.noRoutesAreLoadedOrStarted = function() {
  return this.routes.every(function(route) {
    return (route.timestampLoadConfirmed === null && route.timestampStart === null);
  });
};

RoutePlan.prototype.allRoutesAreAssigned = function() {
  return this.routes.every(function(route) {
    var count = route.countCustomersNotCancelled();
    return !! route.driverUsername || route.countCustomersNotCancelled() === 0; // Has driver (or empty)
  });
};

RoutePlan.prototype.length = function() {
  return routeStats(this.routes, 'length');
};

RoutePlan.prototype.durationDriving = function() {
  return routeStats(this.routes, 'durationDriving');
};

RoutePlan.prototype.durationTotal = function() {
  return routeStats(this.routes, 'durationTotal');
};

var routeStats = function(routes, property) {
  return routes.reduce(function(sum, route) {
    return sum + route[property];
  }, 0);
};

RoutePlan.prototype.countStopLocationsNotCancelled = function() {
  return countAggregated(this.routes, 'countStopLocationsNotCancelled');
};

RoutePlan.prototype.countCustomers = function() {
  return countAggregated(this.routes, 'countCustomers');
};

RoutePlan.prototype.countCustomersDelivered = function() {
  return countAggregated(this.routes, 'countCustomersDelivered');
};

RoutePlan.prototype.countCustomersCancelled = function() {
  return countAggregated(this.routes, 'countCustomersCancelled');
};

RoutePlan.prototype.countCustomersDeviation = function() {
  return countAggregated(this.routes, 'countCustomersDeviation');
};

RoutePlan.prototype.countCustomersNotCancelled = function() {
  return countAggregated(this.routes, 'countCustomersNotCancelled');
};

RoutePlan.prototype.countDeliveries = function() {
  return countAggregated(this.routes, 'countDeliveries');
};

RoutePlan.prototype.countDeliveriesNotCancelled = function() {
  return countAggregated(this.routes, 'countDeliveriesNotCancelled');
};

RoutePlan.prototype.countStopLocationsDone = function() {
  return countAggregated(this.routes, 'countStopLocationsDone');
};

RoutePlan.prototype.countCustomersDone = function() {
  return countAggregated(this.routes, 'countCustomersDone');
};

RoutePlan.prototype.countDeliveriesDone = function() {
  return countAggregated(this.routes, 'countDeliveriesDone');
};

var countAggregated = function(routes, func) {
  return routes.reduce(function(count, route) {
    return count + route[func]();
  }, 0);
};

RoutePlan.prototype.countDeliveries = function(status) {
  return this.routes.reduce(function(count, route) {
    return count + route.countDeliveries(status);
  }, 0);
};

RoutePlan.prototype.countDelayedUnfinishedRoutes = function() {
  return this.routes.reduce(function(count, route) {
    return count + ((route.delayForHumans() && ! route.timestampStop && !! route.driverUsername) ? 1 : 0);
  }, 0);
};

RoutePlan.prototype.updateDelivery = function(newDelivery) {
  var oldDelivery = this.findDelivery(newDelivery.customerId, newDelivery.id);
  if (oldDelivery) {
    angular.extend(oldDelivery, newDelivery);
  } else {
    // Could not find delivery in cache, let's add it to the customer
    var customer = this.findCustomer(newDelivery.customerId);
    if (customer) {
      customer.deliveries.push(newDelivery);
    }
  }
};

RoutePlan.prototype.deleteDelivery = function(deliveryId, customerId) {
  var customer = this.findCustomer(customerId);
  if (customer) {
    customer.deliveries = customer.deliveries.filter(function(delivery) {
      return delivery.id !== deliveryId;
    });
  }
};

RoutePlan.prototype.updateDeliveryEstimates = function(estimates) {
  estimates.forEach(function(estimate) {
    var customer = this.findCustomer(estimate.id);
    customer && customer.updateDeliveryEstimate(estimate);
  }, this);
};

RoutePlan.prototype.updateCustomerSequence = function(routeId, customerSequences) {
  var route = this.findRoute(routeId);
  customerSequences.forEach(function(custSeq) {
    var customer = this.findCustomer(custSeq.id);
    customer && customer.updateRouteSequence(custSeq);

    var stopLocation = route.findStopLocation(custSeq.stopLocationId);
    stopLocation && stopLocation.updateRouteSequence(custSeq);

  }, this);
  route.sortStopLocations();
};

RoutePlan.prototype.updateNotification = function(newNotification) {
  var customer = this.findCustomer(newNotification.customerId);
  if (customer) {
    customer.notifications.push(newNotification);
  }
};

RoutePlan.prototype.updateCustomerImage = function(customerId, imageUrl) {
  var customer = this.findCustomer(customerId);
  if (customer) {
    customer.imageUrl = imageUrl;
  }
};

RoutePlan.prototype.updateCustomerClientTime = function(customerId, clientSideTimestampDone) {
  var customer = this.findCustomer(customerId);
  if (customer) {
    customer.clientSideTimestampDone = clientSideTimestampDone;
  }
};

RoutePlan.prototype.updateCustomerCallLog = function(customerId, driverUsername, timestampCalled) {
  var customer = this.findCustomer(customerId);
  if (customer) {
    customer.callLogs.push({ driverUsername: driverUsername, timestampCalled: timestampCalled });
  }
};

RoutePlan.prototype.updateRoute = function(newRoute) {
  var oldRoute = this.findRoute(newRoute.id);
  if (oldRoute) {
    angular.extend(oldRoute, newRoute, { stopLocations: oldRoute.stopLocations });
    angular.extend(oldRoute, new Count(oldRoute.stopLocations));
    oldRoute.updateCustomersRouteNameAndColor();
  }
};

RoutePlan.prototype.findDelivery = function(customerId, deliveryId) {
  var customer = this.findCustomer(customerId);
  return customer && customer.findDelivery(deliveryId);
};

RoutePlan.prototype.findCustomer = function(customerId) {
  return this.customers.filter(function(customer) {
    return customer.id === customerId;
  })[0];
};

RoutePlan.prototype.findStopLocation = function(routeId, stopLocationId) {
  var route = this.findRoute(routeId);
  return route && route.stopLocations.filter(function(stopLocation) {
    return stopLocation.id === stopLocationId;
  })[0];
};

RoutePlan.prototype.findRoute = function(routeId) {
  return this.routes.filter(function(route) {
    return route.id === routeId;
  })[0];
};

RoutePlan.prototype.findFile = function(fileId) {
  return this.files.filter(function(file) {
    return file.id === fileId;
  })[0];
};

RoutePlan.prototype.pianoFile = function() {
  if (this.files[0] && this.files[0].format === 'PIANO') {
    return this.files[0];
  }
};

RoutePlan.prototype.pickupLocationAddress = function(pickupLocation) {
  return pickupLocation.address + ', ' +
         util.formatZip(pickupLocation.zip, pickupLocation.country) + ' ' +
         pickupLocation.city;
};

RoutePlan.prototype.status = function() {
  switch (this.statusId) {
  case 0:
    return this.deliveryCount ? 'imported' : 'created';
  case 8:
    return 'importing';
  case 1:
    return 'queued';
  case 2:
  case 3:
    return 'optimizing';
  case 4:
    return 'generating';
  case 7:
    return 'failed';
  case 6:
    return 'deleted';
  case 9:
    return 'routesimportqueued';
  case 10:
    return 'routesimporting';
  case 11:
    return this.published ? 'published' : (this.readyForDriverInput ? 'readyfordriverinput' : 'routesimported');
  default:
    return this.published ? 'published' : (this.readyForDriverInput ? 'readyfordriverinput' : 'optimized');
  }
};

RoutePlan.prototype.editable = function() {
  var status = this.status();

  return status === 'created' ||
         status === 'imported' ||
         status === 'importing' ||
         status === 'failed';
};

RoutePlan.prototype.canCustomerBeEdited = function() {
  var status = this.status();

  return status === 'created' ||
         status === 'imported' ||
         status === 'importing' ||
         status === 'routesimported' ||
         status === 'readyfordriverinput' ||
         status === 'published' ||
         status === 'optimized' ||
         status === 'failed';
};

RoutePlan.prototype.canCustomerBeAdded = function(routeId) {
    var route = this.findRoute(routeId);

    return (!! route && !! route.estimatedTimestampStart) && moment(route.estimatedTimestampStart).subtract(120, 'minutes').isAfter(moment());
};

RoutePlan.prototype.correctable = function(routeId, deliveries) {
  var route = this.findRoute(routeId);
  var status = this.status();

  if (! route)
    return true; // If we can't find the route (on this plan), then it should be ok to correct any problems

  // Matching logic in App : can't show loading list untill it's only TWO HOURS left to estimated start
  var moreThanTwoHourBeforeEstimatedStart = (!! route && !! route.estimatedTimestampStart) && moment(route.estimatedTimestampStart).subtract(120, 'minutes').isAfter(moment());

  //console.log('correctable', routeId, status, route.timestampLoadConfirmed, moreThanTwoHourBeforeEstimatedStart);

  return status === 'optimized' || status === 'routesimported' || status === 'readyfordriverinput' ||
    (status === 'published' && (route && ! route.timestampLoadConfirmed && ! route.timestampStart && moreThanTwoHourBeforeEstimatedStart)) ||
    (status === 'published' && deliveries.every(function(delivery) {
          return (delivery.status !== 'delivered' && delivery.status !== "deviation");
      }));
};

RoutePlan.prototype.routesThatNeedOptimization = function() {
  return (this.routes || []).filter(function(route) {
    return route.optimizationStatus === -1;
  });
};

RoutePlan.prototype.deletable = function() {
  var status = this.status();

  return status === 'created' ||
         status === 'imported' ||
         status === 'queued' ||
         status === 'failed' ||
         status === 'optimized' ||
         status === 'routesimported' ||
         status === 'readyfordriverinput';
};

RoutePlan.prototype.sumPrints = function(prints) {
  var totalPrints = 0;
  prints.forEach(function(print) {
    totalPrints += print.numberOfPrints;
  });
  return totalPrints;
};

RoutePlan.prototype.boundaryBoxUrl = function(boundaryBox) {
  if (boundaryBox && boundaryBox.minX) {
    return 'http://bboxfinder.com/#' + boundaryBox.minY + ',' + boundaryBox.minX + ',' + boundaryBox.maxY + ',' + boundaryBox.maxX;
  }
  return '';
};

RoutePlan.prototype.errorMessage = function(key, boundaryBox) {
  if (key === 'company.boundarybox.missing') {
    return {
      title: 'planCustomersBoundaryBoxMissingErrorHeader',
      message: t('planCustomersBoundaryBoxMissingError')
    };
  } else if (key === 'company.boundarybox.outside') {
    return {
      title: 'planCustomersBoundaryBoxOutsideErrorHeader',
      message: t('planCustomersBoundaryBoxOutsideError'),
      link: '<a href="' + this.boundaryBoxUrl(boundaryBox) + '" target="_blank">' + t('settingsCompanyBoundaryBoxLink') + '</a>'
    };
  } else if (key === 'optimize.relocate.products.unbalanced') {
    return {
      title: 'planCustomersRelocateUnbalancedErrorHeader',
      message: t('planCustomersRelocateUnbalancedError')
    };
  }
  return { title: '', message: '' };
};

module.exports = RoutePlan;



/*****************
 ** WEBPACK FOOTER
 ** ./src/js/lib/routePlan.js
 ** module id = 33
 ** module chunks = 1
 **/